// frontend/src/components/Chatbot/ChatMessages.tsx
// Renders the chat message list, loading/streaming indicators, error display,
// and empty states.

import { type RefObject } from 'react';
import { Loader2, StopCircle } from 'lucide-react';
import MessageContent from './MessageContent';
import type { ChatMessage } from '../../hooks/chat/utils';

interface MessageWithExtras extends ChatMessage {
  usage?: { output_tokens?: number };
  responseTime?: number;
  modelInfo?: { model_used?: string };
}

export interface ChatMessagesProps {
  messages: MessageWithExtras[];
  showSmartQuestions: boolean;
  /** Greeting for an empty chat, replacing the generic one — e.g. the
   *  first-visit nudge for a plan with no programs. Rendered locally as an
   *  assistant bubble; it is never sent to the model. */
  welcomeMessage?: string | null;
  isLoading: boolean;
  isStreaming: boolean;
  cancelStreaming: () => void;
  error: string | null;
  messagesContainerRef: RefObject<HTMLDivElement>;
  messagesEndRef: RefObject<HTMLDivElement>;
}

const ChatMessages = ({
  messages,
  showSmartQuestions,
  welcomeMessage,
  isLoading,
  isStreaming,
  cancelStreaming,
  error,
  messagesContainerRef,
  messagesEndRef,
}: ChatMessagesProps) => {
  return (
    <div
      ref={messagesContainerRef}
      role="log"
      aria-live="polite"
      className="mb-4 flex-1 min-h-0 overflow-y-auto space-y-3"
    >
      {messages.length === 0 && !showSmartQuestions && (
        <div className="text-center mt-8 px-4">
          <div className="text-gray-500 text-sm">
            Start a conversation by asking a question below.
          </div>
        </div>
      )}

      {messages.length === 0 && showSmartQuestions && (
        <div className="flex justify-start">
          <div className="max-w-[80%] bg-surface-card border border-line-soft shadow-sm text-ink rounded-lg px-4 py-2">
            <div className="text-sm">
              {welcomeMessage ||
                'Welcome! How can I help you with your academic planning today?'}
            </div>
          </div>
        </div>
      )}

      {messages.map((message) => (
        <div
          key={message.id}
          className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
        >
          <div
            className={`max-w-[80%] rounded-lg px-4 py-2 ${
              message.role === 'user' ? 'bg-primary-strong text-white' : 'bg-surface-card border border-line-soft shadow-sm text-ink'
            }`}
          >
            <MessageContent message={message} />

            {message.role === 'assistant' && message.usage && (
              <div className="mt-2 text-xs text-ink-2 flex items-center justify-between">
                <span>
                  {message.usage.output_tokens} tokens
                  {message.responseTime != null &&
                    ` • ${message.responseTime.toFixed(1)}s`}
                </span>
                {message.modelInfo?.model_used && (
                  <span className="text-xs">{message.modelInfo.model_used}</span>
                )}
              </div>
            )}
          </div>
        </div>
      ))}

      {isLoading && !isStreaming && (
        <div className="flex justify-start">
          <div className="bg-surface-card border border-line-soft shadow-sm rounded-lg px-4 py-3">
            <div className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-gray-600" />
              <span className="text-sm text-gray-600 animate-pulse">Thinking...</span>
            </div>
            <div className="text-xs text-gray-400 mt-1">Analyzing your academic plan</div>
          </div>
        </div>
      )}

      {isStreaming && (
        <div className="flex justify-start">
          <div className="bg-surface-card border border-line-soft shadow-sm rounded-lg px-3 py-1.5 flex items-center gap-2">
            <Loader2 className="w-3 h-3 animate-spin text-gray-500" />
            {/* "Streaming" only once text is arriving; before that the
                bubble above shows what the assistant is doing. */}
            <span className="text-xs text-gray-500">
              {messages.some(m => m.isStreaming && m.content) ? 'Streaming...' : 'Working...'}
            </span>
            <button
              onClick={cancelStreaming}
              className="text-gray-400 hover:text-red-500 transition-colors"
              title="Stop generating"
            >
              <StopCircle className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="flex justify-center">
          <div className="bg-red-50 text-red-700 rounded-lg px-4 py-2 text-sm">
            Error: {error}
          </div>
        </div>
      )}

      <div ref={messagesEndRef} />
    </div>
  );
};

export default ChatMessages;
